/**
 * Firestore Data Serialization Utilities
 *
 * Handles serialization and deserialization of Firestore-specific types
 * (like Timestamps) for localStorage persistence with React Query.
 *
 * Firestore Timestamps don't serialize to JSON directly, so we convert
 * them to a special format that can be restored on hydration.
 */

import { Timestamp } from 'firebase/firestore';

// Marker to identify serialized Timestamps in JSON
const TIMESTAMP_MARKER = '__firestore_timestamp__';

/**
 * Check if a value is a Firestore Timestamp
 */
function isFirestoreTimestamp(value: unknown): value is Timestamp {
  return (
    value instanceof Timestamp ||
    (value !== null &&
      typeof value === 'object' &&
      'toDate' in value &&
      'seconds' in value &&
      'nanoseconds' in value)
  );
}

/**
 * Serialize Firestore data for localStorage storage.
 * Converts Timestamps to a special format that can be restored.
 *
 * @param data - The data to serialize (can be nested objects/arrays)
 * @returns Serialized data safe for JSON.stringify
 */
export function serializeFirestoreData<T>(data: T): T {
  if (data === null || data === undefined) {
    return data;
  }

  // Handle Firestore Timestamp
  if (isFirestoreTimestamp(data)) {
    return {
      [TIMESTAMP_MARKER]: true,
      seconds: (data as Timestamp).seconds,
      nanoseconds: (data as Timestamp).nanoseconds,
    } as unknown as T;
  }

  // Handle Date objects
  if (data instanceof Date) {
    return {
      [TIMESTAMP_MARKER]: true,
      seconds: Math.floor(data.getTime() / 1000),
      nanoseconds: (data.getTime() % 1000) * 1000000,
    } as unknown as T;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => serializeFirestoreData(item)) as unknown as T;
  }

  // Handle plain objects
  if (typeof data === 'object') {
    const serialized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      serialized[key] = serializeFirestoreData(value);
    }
    return serialized as T;
  }

  // Primitives pass through unchanged
  return data;
}

/**
 * Deserialize data from localStorage, restoring Firestore Timestamps.
 *
 * @param data - The data to deserialize
 * @returns Data with Timestamps restored
 */
export function deserializeFirestoreData<T>(data: T): T {
  if (data === null || data === undefined) {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => deserializeFirestoreData(item)) as unknown as T;
  }

  // Handle objects
  if (typeof data === 'object') {
    // Check if this is a serialized Timestamp
    if (TIMESTAMP_MARKER in data) {
      const { seconds, nanoseconds } = data as unknown as {
        seconds: number;
        nanoseconds: number;
      };
      return new Timestamp(seconds, nanoseconds) as unknown as T;
    }

    // Recursively deserialize object properties
    const deserialized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      deserialized[key] = deserializeFirestoreData(value);
    }
    return deserialized as T;
  }

  // Primitives pass through unchanged
  return data;
}

/**
 * Create a custom serializer for React Query persist client.
 * This handles the full serialization/deserialization cycle.
 *
 * Note: The deserialize function returns PersistedClient type which
 * React Query's persister expects.
 */
export function createFirestoreSerializer() {
  return {
    serialize: (data: unknown): string => {
      return JSON.stringify(serializeFirestoreData(data));
    },
    deserialize: (data: string) => {
      try {
        const parsed = JSON.parse(data);
        // Deserialize and cast to the expected type
        // The persister will handle type validation
        return deserializeFirestoreData(parsed);
      } catch {
        return undefined;
      }
    },
  };
}
